import React, { ReactElement, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Table as AntTable, TableProps as AntTableProps, InputRef, Space } from 'antd';
import { ColumnType as AntColumnType, FilterConfirmProps } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { Button } from 'common/components/antd/Button/Button';
import { Input } from 'common/components/antd/Input/Input';
import { SResizeHandle, SResizeTh, SWrapper } from './Table.style';
import { Select } from '../Select/Select';

const ResizableTitle = (
  props: React.HTMLAttributes<any> & {
    onResize: (e: React.SyntheticEvent<Element>, data: ResizeCallbackData) => void;
    width: number;
  },
): ReactElement => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable width={width} height={0} handle={<SResizeHandle />} onResize={onResize}>
      <SResizeTh {...restProps} />
    </Resizable>
  );
};

export type ColumnType<RecordType> = AntColumnType<RecordType> & {
  searchable?: boolean;
  children?: any;
};

export type ColumnsType<RecordType> = ColumnType<RecordType>[];

type TableProps<RecordType> = Omit<AntTableProps<RecordType>, 'columns'> & {
  columns?: ColumnsType<RecordType>;
};

export const Table = <RecordType extends object = any>({
  columns: passedColumns,
  onRow,
  rootClassName,
  components,
  pagination,
  bordered = true,
  ...restProps
}: TableProps<RecordType>): ReactElement => {
  const [columnWidths, setColumnWidths] = useState(passedColumns?.map((column) => column.width) || []);
  const { t } = useTranslation();

  const searchInput = useRef<InputRef>(null);

  const handleReset = (clearFilters: () => void): void => {
    clearFilters();
  };

  const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void): void => {
    confirm();
  };

  const getColumnSearchProps = (col: ColumnType<RecordType>): ColumnType<RecordType> => {
    const dataIndex = col.dataIndex as keyof RecordType;
    if (col.searchable && typeof dataIndex !== 'undefined') {
      return {
        // eslint-disable-next-line react/no-unstable-nested-components
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
          <div
            role="searchbox"
            tabIndex={0}
            style={{ padding: 8 }}
            onKeyDown={(e): void => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              placeholder={t('ui.table.search.placeholder') as string}
              value={selectedKeys[0]}
              onChange={(e): void => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={(): void => {
                handleSearch(selectedKeys as string[], confirm);
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={(): void => {
                  handleSearch(selectedKeys as string[], confirm);
                }}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                {t('ui.table.button.search')}
              </Button>
              <Button
                onClick={(): void => {
                  if (clearFilters) handleReset(clearFilters);
                }}
                size="small"
                style={{ width: 90 }}
              >
                {t('ui.table.button.reset')}
              </Button>
              <Button
                type="link"
                size="small"
                onClick={(): void => {
                  confirm({ closeDropdown: false });
                }}
              >
                {t('ui.table.button.filter')}
              </Button>
              <Button
                type="link"
                size="small"
                onClick={(): void => {
                  close();
                }}
              >
                {t('ui.table.button.close')}
              </Button>
            </Space>
          </div>
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#000000' : undefined }} />,
        onFilter: (value, record) =>
          col.onFilter
            ? col.onFilter(value, record)
            : `${record[dataIndex]}`.toLowerCase().includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible: boolean): void => {
          if (visible) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
      };
    }
    return {};
  };

  const handleResize: Function =
    (index: number) =>
    (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newWidths = [...columnWidths];
      newWidths[index] = size.width;
      setColumnWidths(newWidths);
    };

  const mergeColumns: ColumnsType<RecordType> = (passedColumns || []).map((col, index) => ({
    ...col,
    width: columnWidths[index] || col.width,
    onHeaderCell: (column: ColumnsType<RecordType>[number]) => ({
      width: columnWidths[index] || column.width,
      onResize: handleResize(index) as React.ReactEventHandler<any>,
    }),
    ...getColumnSearchProps(col),
  }));

  return (
    <SWrapper>
      <AntTable
        rootClassName={['custom-ant-table', rootClassName].join(' ')}
        {...restProps}
        pagination={
          !!pagination && {
            selectComponentClass: Select,
            locale: {
              page: t('ui.table.pagination.page') as string,
              items_per_page: t('ui.table.pagination.itemsPerPage') as string,
              jump_to: t('ui.table.pagination.jumpTo') as string,
            },
            ...pagination,
          }
        }
        locale={{
          triggerAsc: t('ui.table.sort.triggerAsc') as string,
          triggerDesc: t('ui.table.sort.triggerDesc') as string,
          cancelSort: t('ui.table.sort.cancelSort') as string,
        }}
        components={{
          header: {
            cell: ResizableTitle,
          },
          ...components,
        }}
        onRow={onRow}
        columns={mergeColumns}
        bordered={bordered}
      />
    </SWrapper>
  );
};
